import React, { useMemo, useState, useEffect } from 'react';
import { ArrowLeft, ChevronDown, ChevronRight, FileText, Link2, Loader2, Plus, Trash2, UserCheck, Users } from 'lucide-react';
import Select from '../../../common/Select';
import { useCountryCodeSelection } from '../../../../hooks/useCountryCodeSelection';
import { getExpectedLength, validateMobile } from '../../../../utils/countryCodes';
import { kycFlowService } from '../../../../services/api';
import { AadharValidator } from '../../../../utils/aadhar';
import { CHEQUE, Chip, SubmitNotice, ackedStatus, DocUpload, DocViewer, Flow, FlowUnit, Label, MOU_TYPES, SelectField, SHOW_MOU_TYPE, STANDARD, inputCls, mouTypeLabel } from './shared';

// Unit-wise nominees. While the profile is a DRAFT each unit is edited here; after
// submission the list is read-only and any unit our team flagged links to its
// /nominee-correction page. Each unit has one nominee (100% of it) and an MOU
// type the investor picks, unless FarmVest has already set it.

interface NomineeForm {
    key: string;
    nominee_full_name: string;
    mobile_no: string;
    // Dial code for mobile_no, e.g. "+91".
    country_code: string;
    relation: string;
    gender: string;
    aadhar_number: string;
    pancard_number: string;
    aadhar_photo_url: string;
    pancard_photo_url: string;
    guardian_certificate_link: string;
    pct: string;
    fromSaved?: boolean;
    // Details our records already hold for this person (utils/nominee_profile): left
    // blank here, filled in by the server on save, never shown to the investor.
    onFile: string[];
}

const ON_FILE_LABELS: Record<string, string> = {
    mobile_no: 'mobile number', pancard_number: 'PAN', aadhar_photo_url: 'Aadhaar card photo', pancard_photo_url: 'PAN card photo',
};

/** MOU status, document and type for a unit. */
const FarmvestInfo: React.FC<{ unit: FlowUnit; compact?: boolean }> = ({ unit, compact }) => {
    // MOU details (status, document, type) are hidden for now, with the MOU type.
    if (!SHOW_MOU_TYPE) return null;
    const f = unit.farmvest;
    const mou = f?.mou_given === true
        ? { text: 'MOU given', cls: 'bg-emerald-100 text-emerald-900' }
        : f?.mou_given === false ? { text: 'MOU not given', cls: 'bg-red-50 text-red-800' }
            : { text: 'MOU status pending', cls: 'bg-slate-100 text-slate-600' };
    const link = f?.mou_link && (
        <DocViewer url={f.mou_link} label="MOU" stopPropagation
            className="inline-flex items-center gap-1 text-[12px] font-bold text-[#1B4332] underline"><FileText size={13} />View MOU</DocViewer>
    );
    if (compact) {
        return (
            <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex h-6 px-2.5 items-center rounded-full text-[11px] font-bold ${mou.cls}`}>{mou.text}</span>
                {link}
            </div>
        );
    }
    return (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 rounded-xl bg-slate-50 border border-slate-200 text-[13px]">
            <div><div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">MOU</div>
                <span className={`mt-1 inline-flex h-6 px-2.5 items-center rounded-full text-[11px] font-bold ${mou.cls}`}>{mou.text}</span></div>
            <div><div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">MOU document</div>
                <div className="mt-1">{link || <span className="text-slate-500">Not shared yet</span>}</div></div>
            {SHOW_MOU_TYPE && <div><div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">MOU type</div>
                <div className="mt-1 font-semibold">{mouTypeLabel(unit.mou_type) || <span className="font-normal text-slate-500">Not chosen yet</span>}</div></div>}
        </div>
    );
};

const COLORS = ['#1B4332', '#52B788', '#B7791F'];
const GENDERS = ['Female', 'Male', 'Other'];

const blank = (pct = ''): NomineeForm => ({
    key: Math.random().toString(36).slice(2), nominee_full_name: '', mobile_no: '', country_code: '+91', relation: '', gender: '',
    aadhar_number: '', pancard_number: '', aadhar_photo_url: '', pancard_photo_url: '',
    guardian_certificate_link: '', pct, onFile: [],
});

const fromNominee = (n: Record<string, any>, pct: any, fromSaved = false): NomineeForm => ({
    ...blank(pct != null ? String(pct) : ''),
    nominee_full_name: n.nominee_full_name || '', mobile_no: n.mobile_no || '', country_code: n.country_code || '+91', relation: n.relation || '',
    gender: n.gender || '', aadhar_number: n.aadhar_number || '', pancard_number: n.pancard_number || '',
    aadhar_photo_url: n.aadhar_photo_url || '',
    pancard_photo_url: n.pancard_photo_url || '', guardian_certificate_link: n.guardian_certificate_link || '', fromSaved,
    onFile: n.on_file || [],
});

// A field the investor may leave blank because it's on file (shown instead of a value).
const onFile = (n: { onFile?: string[] }, k: string) => !!n.onFile?.includes(k);
const ON_FILE_NOTE = 'On file — leave blank to use it, or enter a new one';

const nomineeErrors = (n: NomineeForm, investorAadhaar?: string): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!/^[A-Za-z][A-Za-z .]{1,99}$/.test(n.nominee_full_name.trim())) e.nominee_full_name = 'Enter the full name (letters only)';
    // India: 10 digits starting 6-9; other countries: that country's length (Firestore country_codes).
    const mobileOk = n.country_code === '+91' ? /^[6-9]\d{9}$/.test(n.mobile_no) : validateMobile(n.country_code, n.mobile_no);
    if (!(onFile(n, 'mobile_no') && !n.mobile_no) && !mobileOk) e.mobile_no = n.country_code === '+91' ? 'Enter a valid 10-digit mobile number' : 'Enter a valid mobile number';
    if (!n.relation) e.relation = 'Select a relationship';
    if (!n.gender) e.gender = 'Select a gender';
    if (!/^[2-9]\d{11}$/.test(n.aadhar_number) || !AadharValidator.validate(n.aadhar_number)) e.aadhar_number = 'Enter a valid 12-digit Aadhaar number';
    else if (investorAadhaar && n.aadhar_number === investorAadhaar) e.aadhar_number = 'You cannot be your own nominee';
    if (n.pancard_number && !/^[A-Z]{5}\d{4}[A-Z]$/.test(n.pancard_number)) e.pancard_number = 'Format: ABCDE1234F';
    if (!n.aadhar_photo_url && !onFile(n, 'aadhar_photo_url')) e.aadhar_photo_url = 'Upload the Aadhaar card photo';
    if (n.relation === 'Guardian' && !n.guardian_certificate_link) e.guardian_certificate_link = 'A guardian certificate is required';
    const p = Number(n.pct);
    if (!n.pct || isNaN(p) || p < 1 || p > 100) e.pct = 'Enter a share between 1 and 100';
    return e;
};

// Under the nominee's Aadhaar field: once all 12 digits are in, looks the number up
// (a saved nominee, an investor, or a registered nominee) and offers "Use this" to
// fill the form with what's on file. The number the form opened with isn't looked up.
const AadhaarMatch: React.FC<{ token: string; aadhaar: string; investorAadhaar?: string; onUse: (n: NomineeForm) => void }> = ({ token, aadhaar, investorAadhaar, onUse }) => {
    const [skip, setSkip] = useState(aadhaar);
    const [searching, setSearching] = useState(false);
    const [match, setMatch] = useState<any>(null);
    // Which record's details to use when several hold the Aadhaar ('' = all of them combined).
    const [optId, setOptId] = useState('');
    useEffect(() => {
        setMatch(null); setOptId('');
        if (aadhaar === skip || !/^[2-9]\d{11}$/.test(aadhaar) || !AadharValidator.validate(aadhaar) || aadhaar === investorAadhaar) return;
        let cancelled = false;
        setSearching(true);
        kycFlowService.searchNominee(token, aadhaar).then(res => {
            if (cancelled) return;
            setSearching(false);
            setMatch(res.error ? null : res.data?.match || null);
        });
        return () => { cancelled = true; setSearching(false); };
    }, [aadhaar, skip, token, investorAadhaar]);

    if (searching) return <p className="mt-1.5 text-xs font-semibold text-slate-600 flex items-center gap-1.5" role="status"><Loader2 size={13} className="animate-spin" />Checking for an existing nominee…</p>;
    if (!match) return null;
    const options: any[] = match.options || [];
    const profile = options.find(o => o.id === optId)?.profile || match.profile;
    const use = () => {
        onUse(match.kind === 'saved' ? fromNominee(match.nominee, '', true) : fromNominee({ ...profile, aadhar_number: aadhaar }, ''));
        setSkip(aadhaar);
    };
    // The record shown: a saved nominee, or the chosen (or combined) investor / nominee record.
    const shown: Record<string, any> = match.kind === 'saved' ? match.nominee : (profile || {});
    const docs = ([['Aadhaar front', shown.aadhar_photo_url], ['Aadhaar back', shown.aadhar_back_photo_url], ['PAN card', shown.pancard_photo_url]] as [string, string][])
        .filter(([, u]) => u);
    const rows: [string, React.ReactNode][] = [
        ['Name', shown.nominee_full_name || (match.kind === 'saved' ? '' : match.display_name)],
        ...(match.kind === 'saved' ? [['Relationship', shown.relation] as [string, React.ReactNode]] : []),
        ['Mobile', shown.mobile_no && <span className="font-mono">{shown.country_code ? `${shown.country_code} ` : ''}{shown.mobile_no}</span>],
        ['Gender', shown.gender],
        ['PAN', shown.pancard_number && <span className="font-mono">{shown.pancard_number}</span>],
        ['Date of birth', shown.dob],
    ];
    return (
        <div className="mt-2 rounded-2xl border-2 border-emerald-300 bg-emerald-50/60 overflow-hidden" role="status">
            <div className="flex items-center gap-2 px-4 pt-3.5">
                <UserCheck size={18} className="text-[#1B4332] shrink-0" />
                <span className="text-[13px] font-bold text-emerald-950">Nominee available with this Aadhaar</span>
                <span className="ml-auto text-[11px] font-bold bg-emerald-100 text-emerald-900 px-2 py-0.5 rounded-full whitespace-nowrap">
                    {match.kind === 'saved' ? 'Your saved nominee' : match.kind === 'investor' ? 'Existing investor' : 'Registered nominee'}</span>
            </div>
            {/* Several records hold this Aadhaar (e.g. an investor and a nominee): pick whose details to use. */}
            {match.kind !== 'saved' && options.length > 1 && (
                <div className="px-4 pt-3">
                    <label htmlFor={`opt-${aadhaar}`} className="block text-[11px] font-bold uppercase tracking-wide text-slate-600 mb-1">Use details from</label>
                    <select id={`opt-${aadhaar}`} value={optId} onChange={e => setOptId(e.target.value)}
                        className="w-full h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm">
                        <option value="">All records combined</option>
                        {options.map(o => <option key={o.id} value={o.id}>{o.label} — {o.profile.nominee_full_name || 'No name'}</option>)}
                    </select>
                </div>
            )}
            <dl className="mx-4 mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5 rounded-xl bg-white ring-1 ring-emerald-100 px-3.5 py-3 text-[13px]">
                {rows.map(([label, value]) => (
                    <div key={label} className="min-w-0">
                        <dt className="text-[11px] font-semibold text-slate-500">{label}</dt>
                        <dd className="font-semibold text-slate-900 break-words">{value || <span className="font-normal text-slate-400">—</span>}</dd>
                    </div>
                ))}
                {docs.length > 0 && (
                    <div className="col-span-2">
                        <dt className="text-[11px] font-semibold text-slate-500">Documents</dt>
                        <dd className="mt-1 flex flex-wrap gap-2">
                            {docs.map(([l, u]) => (
                                <DocViewer key={l} url={u} label={l}
                                    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg ring-1 ring-slate-200 bg-slate-50 text-[12px] font-semibold text-[#1B4332] hover:bg-slate-100">
                                    <FileText size={13} />{l}</DocViewer>
                            ))}
                        </dd>
                    </div>
                )}
            </dl>
            <div className="flex justify-end px-4 py-3">
                <button type="button" onClick={use} className="min-h-[44px] w-full sm:w-auto px-5 rounded-xl bg-[#1B4332] text-white text-sm font-bold">Use this</button>
            </div>
        </div>
    );
};

const UnitEditor: React.FC<{
    token: string; flow: Flow; unit: FlowUnit; investorAadhaar?: string;
    onBack: () => void; onSaved: (flow: Flow, msg: string) => void; onError: (msg: string) => void;
}> = ({ token, flow, unit, investorAadhaar, onBack, onSaved, onError }) => {
    // A type FarmVest set is locked; otherwise the investor picks (Buy Back 70L to start).
    const locked = !!unit.farmvest?.mou_type_locked;
    const [mouType, setMouType] = useState<string>(unit.mou_type || CHEQUE);
    // Dial codes and each country's digit rules (Firestore country_codes), as in the Orders nominee form.
    const { countryCodes } = useCountryCodeSelection(true);
    const [noms, setNoms] = useState<NomineeForm[]>(() =>
        unit.allocations.length ? unit.allocations.map(a => fromNominee(a.nominee, a.allocation_percentage)) : [blank('100')]);
    const [open, setOpen] = useState(0);
    const [tried, setTried] = useState(false);
    const [saving, setSaving] = useState(false);
    const max = flow.rules.max_nominees_per_unit;
    // One nominee per unit (server rule): they always receive 100%, so there is no
    // shares step, and choosing a nominee replaces the one on the form.
    const single = max <= 1;

    const sum = noms.reduce((a, n) => a + (Number(n.pct) || 0), 0);
    const allErrs = noms.map(n => nomineeErrors(n, investorAadhaar));
    const dupAadhaar = new Set(noms.map(n => n.aadhar_number)).size !== noms.length;
    const allocOk = Math.abs(sum - 100) <= 0.01;

    const upd = (i: number, k: keyof NomineeForm, val: string) => setNoms(prev => prev.map((n, j) => j === i ? { ...n, [k]: val } : n));
    const add = (n: NomineeForm) => {
        if (single) { setNoms([{ ...n, pct: '100' }]); setOpen(0); return; }
        if (noms.length >= max) return;
        setNoms(prev => [...prev, n]);
        setOpen(noms.length);
    };
    const save = async () => {
        setTried(true);
        if (allErrs.some(e => Object.keys(e).length) || !allocOk || dupAadhaar || (SHOW_MOU_TYPE && !mouType)) {
            const bad = allErrs.findIndex(e => Object.keys(e).length);
            if (bad >= 0) setOpen(bad);
            return;
        }
        setSaving(true);
        // One nominee per unit: the server gives them 100% of it.
        const n = noms[0];
        const res = await kycFlowService.saveUnitNominees(token, unit.unit_id, {
            ...(SHOW_MOU_TYPE ? { mou_type: mouType } : {}),
            nominee: {
                nominee_full_name: n.nominee_full_name.trim(), mobile_no: n.mobile_no, country_code: n.country_code, relation: n.relation, gender: n.gender,
                aadhar_number: n.aadhar_number, pancard_number: n.pancard_number || null,
                aadhar_photo_url: n.aadhar_photo_url || null, pancard_photo_url: n.pancard_photo_url || null,
                guardian_certificate_link: n.relation === 'Guardian' ? n.guardian_certificate_link || null : null,
            },
        });
        if (res.error) { setSaving(false); onError(res.error); return; }
        // Still to be submitted: save and submit for review in one go (no separate Submit step).
        if (unit.open) {
            const sub = await kycFlowService.submit(token, 'nominees', unit.unit_id);
            setSaving(false);
            if (sub.error) { onSaved(res.data.flow, `Nominee saved for Unit ${unit.unit_index + 1}`); onError(sub.error); return; }
            onSaved(sub.data.flow, sub.data.message || `Unit ${unit.unit_index + 1} nominee submitted for review`);
            return;
        }
        setSaving(false);
        onSaved(res.data.flow, `Nominees saved for Unit ${unit.unit_index + 1}`);
    };

    return (
        <div>
            <button type="button" onClick={onBack} className="mt-3 inline-flex items-center gap-1.5 h-10 px-2 -ml-2 rounded-lg text-sm font-bold text-[#1B4332] hover:bg-emerald-50"><ArrowLeft size={16} /> All units</button>
            <div className="flex items-center justify-between flex-wrap gap-2 mt-1">
                <h3 className="text-lg font-extrabold">Unit {unit.unit_index + 1} <span className="text-xs font-mono font-medium text-slate-500 ml-1">{unit.order_id}</span></h3>
            </div>
            {unit.open && <SubmitNotice className="mt-3" />}
            <FarmvestInfo unit={unit} />

            {SHOW_MOU_TYPE && <section className="mt-5">
                <h4 className="text-xs font-extrabold tracking-[.08em] uppercase text-[#3F5F4C] mb-3">1 · MOU type</h4>
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="MOU type">
                    {MOU_TYPES.map(t => {
                        const disabled = locked && unit.mou_type !== t;
                        const on = mouType === t;
                        return (
                            <label key={t}
                                className={`min-h-[44px] px-4 rounded-xl border-2 inline-flex items-center gap-2.5 text-sm font-bold transition focus-within:ring-2 focus-within:ring-[#2D6A4F] ${on ? 'border-[#1B4332] bg-[#F4FAF6] text-[#1B4332]' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                                <input type="radio" name={`mou-${unit.unit_id}`} className="sr-only" checked={on} disabled={disabled} onChange={() => setMouType(t)} />
                                <span className={`w-4 h-4 rounded-full border-2 grid place-items-center ${on ? 'border-[#1B4332]' : 'border-slate-400'}`}>{on && <span className="w-2 h-2 rounded-full bg-[#1B4332]" />}</span>
                                {mouTypeLabel(t)}
                            </label>
                        );
                    })}
                </div>
                {locked && <p className="mt-2 text-xs font-semibold text-amber-900 bg-amber-50 px-3 py-2 rounded-lg">FarmVest has set {mouTypeLabel(unit.mou_type)} for this unit.</p>}
            </section>}

            <section className="mt-7">
                {/* Saved nominees as one dropdown (same as the Orders page's "Reuse existing nominee"). */}
                {flow.saved_nominees.length > 0 && (() => {
                    const picked = flow.saved_nominees.find(s => noms.some(n => n.aadhar_number && n.aadhar_number === s.aadhar_number));
                    return (
                        <div className="mt-4">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 grid place-items-center"><Users size={15} /></span>
                                <span className="text-xs font-extrabold tracking-[.08em] uppercase text-slate-500">Reuse existing nominee</span>
                            </div>
                            <Select value={single ? picked?.id || '' : ''}
                                onChange={id => {
                                    const s = flow.saved_nominees.find(x => x.id === id);
                                    if (s) add(fromNominee(s, '', true));
                                    else if (single) add(blank('100'));
                                }}
                                options={[
                                    { value: '', label: single ? '+ Enter new nominee details' : '+ Pick a saved nominee to add' },
                                    ...flow.saved_nominees.map(s => ({
                                        value: s.id,
                                        label: [s.nominee_full_name, s.relation, `Aadhaar ****${String(s.aadhar_number || '').slice(-4)}`].filter(Boolean).join(' — '),
                                        disabled: !single && (noms.length >= max || noms.some(n => n.aadhar_number === s.aadhar_number)),
                                    })),
                                ]}
                                className={`${inputCls()} bg-white`} />
                        </div>
                    );
                })()}
                <div className="mt-3 flex flex-wrap gap-2 items-center">
                    {!single && (
                        <button type="button" onClick={() => add(blank(''))} disabled={noms.length >= max}
                            className="min-h-[40px] px-3 rounded-xl border border-slate-300 bg-white text-[13px] font-bold text-[#1B4332] inline-flex items-center gap-1 disabled:opacity-40"><Plus size={14} /> Enter manually</button>
                    )}
                </div>
                {!single && <p className="mt-2 text-xs text-slate-500">Up to {max} nominees per unit · {noms.length} added</p>}
            </section>

            <section className="mt-7">
                <div className="space-y-3">
                    {noms.map((n, i) => {
                        const e = tried ? allErrs[i] : {};
                        const ok = Object.keys(allErrs[i]).length === 0;
                        const isOpen = open === i;
                        return (
                            <article key={n.key} className={`rounded-2xl border ${isOpen ? 'border-emerald-300' : 'border-slate-200'} bg-white`}>
                                <div className="flex items-center gap-2 p-3">
                                    <button type="button" onClick={() => setOpen(isOpen ? -1 : i)} aria-expanded={isOpen} className="flex-1 min-w-0 flex items-center gap-3 text-left min-h-[44px]">
                                        <span className="w-10 h-10 rounded-full grid place-items-center text-white text-xs font-extrabold shrink-0" style={{ background: COLORS[i % 3] }}>
                                            {(n.nominee_full_name || 'N N').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()}
                                        </span>
                                        <span className="min-w-0">
                                            <span className="block font-bold truncate">{n.nominee_full_name || 'Add Nominee'}</span>
                                            {(n.relation || !single) && <span className="text-xs text-slate-500">{[n.relation, single ? '' : `${n.pct || 0}%`].filter(Boolean).join(' · ')}</span>}
                                        </span>
                                    </button>
                                    <span className={`h-6 px-2.5 rounded-full text-[11px] font-bold inline-flex items-center ${ok ? 'bg-emerald-100 text-emerald-900' : 'bg-slate-100 text-slate-600'}`}>{ok ? 'Ready' : 'Incomplete'}</span>
                                    {!single && (
                                        <button type="button" aria-label={`Remove ${n.nominee_full_name || 'nominee'}`} onClick={() => { setNoms(prev => prev.filter((_, j) => j !== i)); setOpen(0); }}
                                            className="w-10 h-10 rounded-lg grid place-items-center text-slate-500 hover:bg-red-50 hover:text-red-700"><Trash2 size={16} /></button>
                                    )}
                                    <button type="button" aria-label="Toggle details" onClick={() => setOpen(isOpen ? -1 : i)} className="w-10 h-10 rounded-lg grid place-items-center text-slate-500"><ChevronDown size={18} className={isOpen ? 'rotate-180' : ''} /></button>
                                </div>
                                {isOpen && (
                                    <div className="px-4 pb-5 pt-2 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                                            {/* Aadhaar first: an existing nominee with it can fill in the rest. */}
                                            <div className="sm:col-span-2"><Label htmlFor={`na-${n.key}`} required>Aadhaar number</Label>
                                                <input id={`na-${n.key}`} inputMode="numeric" autoComplete="off" value={n.aadhar_number} placeholder="12-digit number" onChange={ev => upd(i, 'aadhar_number', ev.target.value.replace(/\D/g, '').slice(0, 12))} className={`${inputCls(e.aadhar_number)} font-mono sm:max-w-sm`} />
                                                {e.aadhar_number && <p className="mt-1 text-xs font-semibold text-red-700">{e.aadhar_number}</p>}
                                                <AadhaarMatch token={token} aadhaar={n.aadhar_number} investorAadhaar={investorAadhaar}
                                                    onUse={m => setNoms(prev => prev.map((x, j) => j === i ? { ...m, key: x.key, pct: x.pct } : x))} /></div>
                                            <div><Label htmlFor={`nn-${n.key}`} required>Full name</Label>
                                                <input id={`nn-${n.key}`} value={n.nominee_full_name} placeholder="As on Aadhaar" onChange={ev => upd(i, 'nominee_full_name', ev.target.value.replace(/[^A-Za-z .]/g, ''))} className={inputCls(e.nominee_full_name)} />
                                                {e.nominee_full_name && <p className="mt-1 text-xs font-semibold text-red-700">{e.nominee_full_name}</p>}</div>
                                            <div><Label htmlFor={`nm-${n.key}`} required>Mobile number</Label>
                                                {/* Country code (same list as the Orders nominee form) + number. */}
                                                <div className="flex">
                                                    <div className="w-[104px] shrink-0">
                                                        <Select value={n.country_code} onChange={cc => setNoms(prev => prev.map((x, j) => j === i ? { ...x, country_code: cc, mobile_no: x.mobile_no.slice(0, cc === '+91' ? 10 : getExpectedLength(cc)) } : x))}
                                                            options={(countryCodes.length ? countryCodes : [{ code: '+91', country: 'India' }]).map(c => ({ value: c.code, label: `${c.code}${c.country ? ` ${c.country}` : ''}` }))}
                                                            renderValue={sel => <span className="font-bold">{sel?.value || n.country_code}</span>}
                                                            searchable searchPlaceholder="Search country or code"
                                                            panelClassName="max-h-72 overflow-y-auto"
                                                            className={`${inputCls(e.mobile_no)} bg-slate-50 rounded-r-none border-r-0 px-3`} />
                                                    </div>
                                                    <input id={`nm-${n.key}`} inputMode="numeric" value={n.mobile_no} placeholder={n.country_code === '+91' ? '10-digit number' : 'Mobile number'}
                                                        onChange={ev => upd(i, 'mobile_no', ev.target.value.replace(/\D/g, '').slice(0, n.country_code === '+91' ? 10 : getExpectedLength(n.country_code)))}
                                                        className={`${inputCls(e.mobile_no)} rounded-l-none font-mono`} /></div>
                                                {e.mobile_no && <p className="mt-1 text-xs font-semibold text-red-700">{e.mobile_no}</p>}
                                                {!n.mobile_no && onFile(n, 'mobile_no') && <p className="mt-1 text-xs text-emerald-800">{ON_FILE_NOTE}</p>}</div>
                                            <div><Label htmlFor={`nr-${n.key}`} required>Relationship with you</Label>
                                                <SelectField id={`nr-${n.key}`} label="Relationship" value={n.relation} options={flow.rules.relations} error={e.relation}
                                                    placeholder="Select relationship" onChange={v => upd(i, 'relation', v)} />
                                                {e.relation && <p className="mt-1 text-xs font-semibold text-red-700">{e.relation}</p>}</div>
                                            <div><Label htmlFor={`ng-${n.key}`} required>Gender</Label>
                                                {/* Same as the KYC form: three buttons on phones, a dropdown on desktop. */}
                                                <div role="radiogroup" aria-label="Gender" className="lg:hidden grid grid-cols-3 gap-2">
                                                    {GENDERS.map(g => {
                                                        const on = n.gender === g;
                                                        return <button key={g} type="button" role="radio" aria-checked={on} onClick={() => upd(i, 'gender', g)}
                                                            className={`h-14 rounded-xl border text-[15px] font-bold transition ${on ? 'bg-[#1B4332] border-[#1B4332] text-white' : e.gender ? 'bg-white border-red-400 text-slate-800' : 'bg-white border-slate-300 text-slate-800'}`}>{g}</button>;
                                                    })}
                                                </div>
                                                <div className="max-lg:hidden">
                                                    <SelectField id={`ng-${n.key}`} label="Gender" value={n.gender} options={GENDERS} error={e.gender}
                                                        placeholder="Select gender" onChange={v => upd(i, 'gender', v)} />
                                                </div>
                                                {e.gender && <p className="mt-1 text-xs font-semibold text-red-700">{e.gender}</p>}</div>
                                            <div><Label htmlFor={`np-${n.key}`}>PAN</Label>
                                                <input id={`np-${n.key}`} value={n.pancard_number} placeholder="ABCDE1234F" onChange={ev => upd(i, 'pancard_number', ev.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))} className={`${inputCls(e.pancard_number)} font-mono`} />
                                                {e.pancard_number && <p className="mt-1 text-xs font-semibold text-red-700">{e.pancard_number}</p>}
                                                {!n.pancard_number && onFile(n, 'pancard_number') && <p className="mt-1 text-xs text-emerald-800">{ON_FILE_NOTE}</p>}</div>
                                        </div>
                                        {/* Guardian certificate is asked for only when the nominee is a Guardian. */}
                                        <div className={`grid grid-cols-1 gap-3 mt-4 ${n.relation === 'Guardian' ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                                            <DocUpload id={`da-${n.key}`} allowUrl={!!flow.assist} label="Aadhaar card" required url={n.aadhar_photo_url || null} error={e.aadhar_photo_url} mobile={flow.investor.mobile} folder="nominees" onChange={u => upd(i, 'aadhar_photo_url', u || '')} />
                                            <DocUpload id={`dp-${n.key}`} allowUrl={!!flow.assist} label="PAN card" url={n.pancard_photo_url || null} mobile={flow.investor.mobile} folder="nominees" onChange={u => upd(i, 'pancard_photo_url', u || '')} />
                                            {(['aadhar_photo_url', 'pancard_photo_url'] as const).some(k => !n[k] && onFile(n, k)) && (
                                                <p className={`text-xs text-emerald-800 ${n.relation === 'Guardian' ? 'sm:col-span-3' : 'sm:col-span-2'}`}>
                                                    {(['aadhar_photo_url', 'pancard_photo_url'] as const).filter(k => !n[k] && onFile(n, k)).map(k => ON_FILE_LABELS[k]).join(' and ')} on file — no need to upload.</p>
                                            )}
                                            {n.relation === 'Guardian' && (
                                                <DocUpload id={`dg-${n.key}`} allowUrl={!!flow.assist} label="Guardian certificate" required url={n.guardian_certificate_link || null} error={e.guardian_certificate_link} mobile={flow.investor.mobile} folder="nominees" onChange={u => upd(i, 'guardian_certificate_link', u || '')} />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </article>
                        );
                    })}
                    {dupAadhaar && <p className="text-xs font-semibold text-red-700">The same Aadhaar is listed twice on this unit.</p>}
                </div>
            </section>
            <div className="mt-8 pt-5 border-t border-slate-100 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                <button type="button" onClick={onBack} className="min-h-[48px] px-5 rounded-xl border border-slate-300 bg-white font-bold text-[#1B4332]">Cancel</button>
                <button type="button" onClick={save} disabled={saving} className="min-h-[48px] px-6 rounded-xl bg-[#1B4332] hover:bg-[#12301F] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    {saving && <Loader2 size={16} className="animate-spin" />}{unit.open ? 'Submit for review' : `Save nominees for Unit ${unit.unit_index + 1}`}
                </button>
            </div>
        </div>
    );
};

interface SectionProps {
    token: string;
    flow: Flow;
    investorAadhaar?: string;
    onFlow: (flow: Flow, msg?: string) => void;
    onError: (msg: string) => void;
    // Tells the page a unit's nominee form is open, so it can hide its own submit bar.
    onEditingChange?: (editing: boolean) => void;
    // This unit's acknowledgment, shown inside its own card (null when there is none).
    unitAck?: (unit: FlowUnit) => React.ReactNode;
}

const NOMINEE_DETAIL_ROWS: { label: string; get: (n: Record<string, any>) => any; mono?: boolean }[] = [
    { label: 'Full name', get: n => n.nominee_full_name },
    { label: 'Relationship', get: n => n.relation },
    { label: 'Mobile', get: n => (n.mobile_no ? `${n.country_code || '+91'} ${n.mobile_no}` : undefined) || (n.on_file?.includes('mobile_no') ? 'On file' : undefined), mono: true },
    { label: 'Aadhaar number', get: n => n.aadhar_number, mono: true },
    { label: 'Gender', get: n => n.gender },
    { label: 'PAN', get: n => n.pancard_number || (n.on_file?.includes('pancard_number') ? 'On file' : undefined), mono: true },
];
const NOMINEE_DOC_ROWS: { label: string; get: (n: Record<string, any>) => string | undefined }[] = [
    { label: 'Aadhaar card', get: n => n.aadhar_photo_url },
    { label: 'PAN card', get: n => n.pancard_photo_url },
    { label: 'Guardian certificate', get: n => n.guardian_certificate_link },
];

/** Read-only view of a unit's nominee — shown when the unit can't be edited any more. */
const NomineeDetails: React.FC<{ nominee: Record<string, any> }> = ({ nominee: n }) => {
    const docs = NOMINEE_DOC_ROWS.filter(d => d.get(n));
    return (
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3.5">
            <dl className="divide-y divide-slate-200/70">
                {NOMINEE_DETAIL_ROWS.map(r => (
                    <div key={r.label} className="flex justify-between gap-3 py-2 text-sm">
                        <dt className="text-slate-500 shrink-0">{r.label}</dt>
                        <dd className={`font-bold text-right break-words min-w-0 ${r.mono ? 'font-mono' : ''}`}>{r.get(n) || <span className="font-normal text-slate-400">Not provided</span>}</dd>
                    </div>
                ))}
            </dl>
            {docs.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                    {docs.map(d => (
                        <DocViewer key={d.label} url={d.get(n) || ''} label={d.label} stopPropagation
                            className="inline-flex items-center gap-2 p-1.5 pr-3 rounded-lg bg-white border border-slate-200 text-xs font-bold text-[#1B4332] hover:border-emerald-300">
                            <img src={d.get(n)} alt={d.label} className="w-12 h-9 object-cover rounded bg-slate-100" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                            {d.label}
                        </DocViewer>
                    ))}
                </div>
            )}
        </div>
    );
};

const NomineesSection: React.FC<SectionProps> = ({ token, flow, investorAadhaar, onFlow, onError, onEditingChange, unitAck }) => {
    const [editing, setEditing] = useState<string | null>(null);
    useEffect(() => { onEditingChange?.(editing !== null); }, [editing, onEditingChange]);
    useEffect(() => () => onEditingChange?.(false), [onEditingChange]);
    // Units whose nominee details are expanded (read-only view).
    const [viewing, setViewing] = useState<Set<string>>(new Set());
    const toggleView = (id: string) => setViewing(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
    const corrByUnit = useMemo(() => Object.fromEntries(flow.nominee_corrections.map(c => [c.unit_id, c])), [flow.nominee_corrections]);
    const done = flow.units.filter(u => !['draft', 'not_started', 'no_nominees'].includes(u.status)).length;
    const unit = flow.units.find(u => u.unit_id === editing);

    // After submission a unit that never had nominees can still be filled (server: save_unit_nominees).
    // Until its nominee is submitted (or while the profile is a DRAFT) a unit stays editable,
    // unless staff verified or flagged it (server re-checks partial verification).
    // MOU given: the unit is frozen (the server refuses the save too).
    const unitEditable = (u: FlowUnit) => !u.mou_locked && (((flow.stage === 'DRAFT' || !!u.open) && !['verified', 'needs_correction'].includes(u.status))
        || (flow.stage === 'SUBMITTED' && !u.allocations.length));
    if (unit && unitEditable(unit)) {
        return <UnitEditor token={token} flow={flow} unit={unit} investorAadhaar={investorAadhaar}
            onBack={() => setEditing(null)} onError={onError}
            onSaved={(f, msg) => { setEditing(null); onFlow(f, msg); }} />;
    }

    if (!flow.units.length) {
        return <div className="mt-6 p-6 rounded-2xl border-2 border-dashed border-slate-300 text-center text-slate-600">Your units will appear here once your order is approved.</div>;
    }

    return (
        <div>
            {flow.units.some(u => u.open) && <SubmitNotice className="mt-4" />}
            <div className="mt-5 p-4 rounded-2xl bg-[#1B4332] text-white">
                <div className="text-xs font-bold text-emerald-200 tracking-wide">UNITS</div>
                <div className="text-xl font-extrabold mt-1">{done} of {flow.units.length} units {flow.stage === 'DRAFT' || flow.nominees_open ? 'complete' : 'verified'}</div>
                <div className="grid gap-1 mt-3" style={{ gridTemplateColumns: `repeat(${flow.units.length}, minmax(0,1fr))` }}>
                    {flow.units.map(u => <span key={u.unit_id} className={`h-2 rounded-full ${['complete', 'verified'].includes(u.status) ? 'bg-emerald-300' : u.status === 'needs_correction' ? 'bg-red-300' : u.status === 'draft' || u.status === 'in_review' ? 'bg-indigo-300' : 'bg-white/25'}`} />)}
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                {flow.units.map(u => {
                    const corr = corrByUnit[u.unit_id];
                    const canEdit = unitEditable(u);
                    const action = canEdit ? (u.allocations.length ? (u.status === 'draft' ? 'Continue' : 'Edit nominees') : 'Add nominee') : null;
                    // Locked units open a read-only view of their nominee instead of the editor.
                    const viewable = !canEdit && u.allocations.length > 0;
                    const open = viewable && viewing.has(u.unit_id);
                    return (
                        <article key={u.unit_id} onClick={viewable ? () => toggleView(u.unit_id) : undefined}
                            className={`p-4 rounded-2xl border bg-white flex flex-col gap-3 ${viewable ? 'cursor-pointer hover:border-emerald-300' : ''} ${open ? 'md:col-span-2' : ''} ${u.status === 'needs_correction' ? 'border-red-300' : open ? 'border-[#1B4332]' : 'border-slate-200'}`}>
                            <div className="flex items-center justify-between"><b>Unit {u.unit_index + 1}</b><Chip status={ackedStatus(u.status, u.ack?.status)} /></div>
                            <div className="flex items-end justify-between gap-2">
                                <div>
                                    {SHOW_MOU_TYPE && u.mou_type && <span className={`inline-flex h-6 px-2.5 items-center rounded-full text-[11px] font-bold ${u.mou_type === STANDARD ? 'bg-emerald-100 text-[#1B4332]' : 'bg-amber-100 text-amber-900'}`}>{mouTypeLabel(u.mou_type)}</span>}
                                    <div className="text-[11px] text-slate-500 font-mono">{u.order_id}</div>
                                </div>
                            </div>
                            <FarmvestInfo unit={u} compact />
                            <div className="text-sm min-h-[40px]">
                                {u.allocations.length ? u.allocations.map(a => (
                                    <div key={a.nominee.id || a.nominee.aadhar_number} className="flex justify-between gap-2"><span className="truncate">{a.nominee.nominee_full_name} · <span className="text-slate-500">{a.nominee.relation}</span></span></div>
                                )) : <span className="text-slate-500">No nominee yet</span>}
                            </div>
                            {open && u.allocations.map(a => <NomineeDetails key={a.nominee.id || a.nominee.aadhar_number} nominee={a.nominee} />)}
                            {viewable && (
                                <button type="button" aria-expanded={open} onClick={e => { e.stopPropagation(); toggleView(u.unit_id); }}
                                    className="min-h-[44px] rounded-xl font-bold text-sm inline-flex items-center justify-center gap-1 border border-slate-300 text-[#1B4332] bg-white">
                                    {open ? 'Hide nominee details' : 'View nominee details'}<ChevronDown size={16} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                                </button>
                            )}
                            {corr && (
                                <div className="text-[13px] bg-red-50 text-red-900 px-3 py-2 rounded-lg"><b>Our team:</b> {corr.remarks}</div>
                            )}
                            {(() => {
                                const ackNode = unitAck?.(u);
                                // Clicks here must not open/close the card's nominee details.
                                return ackNode ? <div onClick={e => e.stopPropagation()}>{ackNode}</div> : null;
                            })()}
                            {action && (
                                <button type="button" onClick={() => setEditing(u.unit_id)} className={`min-h-[44px] rounded-xl font-bold text-sm inline-flex items-center justify-center gap-1 ${u.allocations.length && u.status !== 'draft' ? 'border border-slate-300 text-[#1B4332] bg-white' : 'bg-[#1B4332] text-white'}`}>{action}<ChevronRight size={16} /></button>
                            )}
                            {corr && (
                                <a href={`/nominee-correction?token=${encodeURIComponent(corr.token)}`} onClick={e => e.stopPropagation()} className="min-h-[44px] rounded-xl font-bold text-sm inline-flex items-center justify-center gap-1.5 bg-red-700 text-white"><Link2 size={15} />Fix now</a>
                            )}
                        </article>
                    );
                })}
            </div>
        </div>
    );
};

export default NomineesSection;
